// client/src/components/configuracoes/pagamentos/MetodoPagamentoPage.js
import React, { useState, useCallback, useMemo, Fragment } from 'react';
import { useMetodosPagamento, useMutateMetodosPagamento } from '../../../hooks/useMetodosPagamento';
import { useAuth } from '../../../context/auth/AuthProvider';
import PageHeader from '../../layout/PageHeader';
import FabButton from '../../common/FabButton';
import { PlusCircle, CreditCard, XCircle, Edit, Trash2, Filter as FilterIcon, Search, X, MoreVertical } from 'lucide-react';
import MetodoPagamentoForm from './MetodoPagamentoForm';
import ReportTable from '../../indicadores/ReportTable';
import ActionsDrawer from '../../common/ActionsDrawer';

const MetodoPagamentoPage = () => {
    const [isFormVisible, setIsFormVisible] = useState(false);
    const [current, setCurrent] = useState(null);
    const [showFilters, setShowFilters] = useState(false);
    const [busca, setBusca] = useState('');
    const [tipoFiltro, setTipoFiltro] = useState('');
    const [selectedMetodo, setSelectedMetodo] = useState(null);
    const { user } = useAuth();

    const { data: metodosData, isLoading } = useMetodosPagamento(user?.account?._id);
    const { deleteMetodo } = useMutateMetodosPagamento();
    const isMutating = deleteMetodo.isPending;

    const handleAdicionarClick = useCallback(() => {
        setCurrent(null);
        setIsFormVisible(true);
    }, []);

    const handleCloseForm = useCallback(() => {
        setIsFormVisible(false);
        setCurrent(null);
    }, []);

    const handleEditClick = useCallback((metodo) => {
        setCurrent(metodo);
        setIsFormVisible(true);
        setSelectedMetodo(null);
    }, []);

    const handleDelete = useCallback((id) => {
        if (window.confirm('Tem certeza que deseja excluir esta forma de pagamento?')) {
            deleteMetodo.mutate(id, { onSuccess: () => setSelectedMetodo(null) });
        }
    }, [deleteMetodo]);

    const allMetodos = metodosData?.data || [];

    const metodosFiltrados = useMemo(() => {
        return allMetodos.filter(m => {
            if (busca && !m.nome?.toLowerCase().includes(busca.toLowerCase())) return false;
            if (tipoFiltro && m.tipo !== tipoFiltro) return false;
            return true;
        });
    }, [allMetodos, busca, tipoFiltro]);

    const columns = [
        { key: 'nome', header: 'Nome', render: (row) => row.nome },
        { key: 'tipo', header: 'Tipo', render: (row) => row.tipo === 'PRE_PAGO' ? 'Pré-Pago (Online)' : 'Pós-Pago (no Local)' },
        { key: 'gateway', header: 'Gateway', render: (row) => row.gateway?.name || 'N/A' },
        { key: 'ativo', header: 'Status', render: (row) => row.ativo ? 'Ativo' : 'Inativo' },
        {
            key: 'acoes',
            header: 'Ações',
            render: (row) => (
                <div className="actions-bar" style={{ justifyContent: 'center' }}>
                    <button onClick={() => setSelectedMetodo(row)} className="action-icon-button" title="Ações" disabled={isMutating}>
                        <MoreVertical size={18} />
                    </button>
                </div>
            )
        },
    ];

    const hasFilters = busca !== '' || tipoFiltro !== '';

    return (
        <Fragment>
            <div className="page-management-container">
                <PageHeader
                    icon={<CreditCard />}
                    title="Formas de Pagamento"
                    actions={<>
                        {!isFormVisible && (
                            <>
                                <button
                                    onClick={() => setShowFilters(p => !p)}
                                    className={`action-button ${showFilters ? 'action-button-primary' : 'action-button-secondary'}`}
                                    disabled={isLoading}
                                >
                                    <FilterIcon size={18} className="icon"/>
                                    {showFilters ? 'Ocultar Filtros' : 'Filtros'}
                                </button>
                            </>
                        )}
                        {isFormVisible && (
                            <button onClick={handleCloseForm} className="action-button action-button-secondary">
                                <XCircle size={18} className="icon"/> Fechar
                            </button>
                        )}
                    </>}
                />

                {showFilters && !isFormVisible && (
                    <div className="content-panel">
                        <h3 className="panel-title">Filtrar Formas de Pagamento</h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                            <div className="flex flex-col gap-1">
                                <label className="text-xs font-semibold text-gray-600 uppercase">Buscar Nome</label>
                                <div className="relative">
                                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                    <input
                                        type="text"
                                        value={busca}
                                        onChange={e => setBusca(e.target.value)}
                                        placeholder="Nome da forma de pagamento..."
                                        className="form-input text-sm py-2 pl-10 pr-3 rounded border border-gray-300 outline-none w-full"
                                    />
                                </div>
                            </div>
                            <div className="flex flex-col gap-1">
                                <label className="text-xs font-semibold text-gray-600 uppercase">Tipo</label>
                                <select
                                    value={tipoFiltro}
                                    onChange={e => setTipoFiltro(e.target.value)}
                                    className="form-input text-sm py-2 px-3 rounded border border-gray-300 outline-none bg-white w-full"
                                >
                                    <option value="">Todos os tipos</option>
                                    <option value="PRE_PAGO">Pré-Pago (Online)</option>
                                    <option value="POS_PAGO">Pós-Pago (no Local)</option>
                                </select>
                            </div>
                            {hasFilters && (
                                <button
                                    type="button"
                                    onClick={() => { setBusca(''); setTipoFiltro(''); }}
                                    className="action-button action-button-secondary"
                                >
                                    <X size={16} className="icon"/> Limpar Filtros
                                </button>
                            )}
                        </div>
                    </div>
                )}

                {isFormVisible && (
                    <div className="content-panel">
                        <MetodoPagamentoForm current={current} onSaveSuccess={handleCloseForm} onCloseForm={handleCloseForm} />
                    </div>
                )}

                {!isFormVisible && (
                    <div className="table-container" style={{ marginTop: '1.5rem' }}>
                        <ReportTable
                            title="Formas de Pagamento Cadastradas"
                            columns={columns}
                            data={metodosFiltrados}
                            isLoading={isLoading || isMutating}
                        />
                    </div>
                )}
            </div>

            <FabButton onClick={handleAdicionarClick} title="Adicionar Forma de Pagamento" visible={!isFormVisible} disabled={isLoading} icon={<CreditCard size={26} className="icon"/>} />

            <ActionsDrawer
                open={!!selectedMetodo}
                onClose={() => setSelectedMetodo(null)}
                title={selectedMetodo?.nome || 'Forma de Pagamento'}
                actions={selectedMetodo ? [
                    {
                        key: 'editar',
                        label: 'Editar',
                        icon: <Edit size={20} className="icon" />,
                        onClick: () => handleEditClick(selectedMetodo),
                    },
                    {
                        key: 'excluir',
                        label: 'Excluir',
                        icon: <Trash2 size={20} className="icon" />,
                        onClick: () => handleDelete(selectedMetodo._id),
                        destructive: true,
                        loading: isMutating,
                    },
                ] : []}
            />
        </Fragment>
    );
};

export default MetodoPagamentoPage;
